import React from 'react';
import { Box, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import {
  startOfWeek,
  endOfWeek,
  isWithinInterval,
  isSameDay,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import enLocale from 'date-fns/locale/en-US';
import { getWeekStartEnv } from 'helpers/envGetters';
import { MobileDatePickerProps } from './MobileDatePicker';
import { getWeekForDate } from '../../helpers/date';
import { mobileWeekPickerStyles } from './MobileWeekPicker.styles';

const WEEK_START = getWeekStartEnv();

const MobileWeekPicker = (props: MobileDatePickerProps) => {
  const { startDate, endDate, selectedDay, setActiveDate, closeCalendarModal } = props;

  const currentDate = selectedDay || new Date();
  const weekInfo = getWeekForDate(currentDate);

  // Get the start and end of the selected week
  const weekStart = startOfWeek(currentDate, { weekStartsOn: WEEK_START });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: WEEK_START });

  // Calculate dynamic week number based on current month
  const calculateWeekNumber = (date: Date) => {
    const firstDayOfMonth = startOfMonth(date);
    const lastDayOfMonth = endOfMonth(date);

    // Get the first day of the first week and last day of the last week that contain this month
    const firstWeekStart = startOfWeek(firstDayOfMonth, { weekStartsOn: WEEK_START });
    const lastWeekEnd = endOfWeek(lastDayOfMonth, { weekStartsOn: WEEK_START });

    // Calculate the difference in weeks
    const diffInMs = lastWeekEnd.getTime() - firstWeekStart.getTime();
    const diffInWeeks = Math.ceil(diffInMs / (7 * 24 * 60 * 60 * 1000));

    // Ensure reasonable bounds (typically 4-6 weeks for a month)
    return Math.max(4, Math.min(6, diffInWeeks));
  };

  const dynamicWeekNumber = calculateWeekNumber(currentDate);

  const onChangeDateHandler = (date: Date | null) => {
    if (!date) return;

    setActiveDate?.(date);
    closeCalendarModal?.();
  };
  // Custom day renderer to highlight the entire week
  const renderWeekPickerDay = (pickersDayProps: any) => {
    const { day, ...other } = pickersDayProps;
    const isInSelectedWeek = isWithinInterval(day, { start: weekStart, end: weekEnd });

    // Check if this is the first or last day of the week for slight border radius
    // Use isSameDay to compare only date parts, not time
    const isFirstDayOfWeek = isSameDay(day, weekStart);
    const isLastDayOfWeek = isSameDay(day, weekEnd);

    const dayStyles = {
      ...(isInSelectedWeek && {
        ...mobileWeekPickerStyles.pickerDay.selectedWeek,
        ...(isFirstDayOfWeek && mobileWeekPickerStyles.pickerDay.firstDayOfWeek),
        ...(isLastDayOfWeek && mobileWeekPickerStyles.pickerDay.lastDayOfWeek),
      }),
    };

    return <PickersDay {...other} day={day} sx={dayStyles} />;
  };

  return (
    <Box sx={mobileWeekPickerStyles.wrapper} data-id={'OuterWrapper'}>
      <Typography sx={mobileWeekPickerStyles.header}>{weekInfo.title}</Typography>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={{
          ...enLocale,
          options: {
            ...enLocale.options,
            weekStartsOn: WEEK_START,
          },
        }}>
        <DateCalendar
          value={selectedDay || null}
          onChange={onChangeDateHandler}
          minDate={startDate}
          maxDate={endDate}
          showDaysOutsideCurrentMonth={true}
          fixedWeekNumber={dynamicWeekNumber}
          slots={{
            day: renderWeekPickerDay,
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default MobileWeekPicker;
